import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axiosInstance from '../axiosConfig';
import { loadStripe, Stripe } from '@stripe/stripe-js';

// Make sure to call `loadStripe` outside of a component's render to avoid
// recreating the `Stripe` object on every render.
export const stripePromise = loadStripe(process.env.NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY as string);

interface PaygBalance {
  available_credits: number;
  total_spent: number;
}

interface BillingHistoryItem {
  id: string;
  amount: number;
  credits: number;
  type: 'subscription' | 'payg';
  status: string;
  created_at: string;
}

interface PaymentState {
  subscriptionStatus: any;
  usageStats: any;
  paygBalance: PaygBalance | null;
  billingHistory: BillingHistoryItem[];
  isLoading: boolean;
  error: string | null;
  checkoutSessionId: string | null;
  clientSecret: string | null; // Added for Stripe Elements
  isCheckoutModalOpen: boolean; // Control modal visibility
}

const initialState: PaymentState = {
  subscriptionStatus: null,
  usageStats: null,
  paygBalance: null,
  billingHistory: [],
  isLoading: false,
  error: null,
  checkoutSessionId: null,
  clientSecret: null,
  isCheckoutModalOpen: false,
};

// Get base URL for redirects
const getBaseUrl = () => {
  if (typeof window !== 'undefined') {
    return window.location.origin;
  }
  // Fallback to environment variable or hardcoded URL
  return process.env.NEXT_PUBLIC_APP_URL || 'https://ainativeforms.com';
};

// Create a Stripe checkout session
export const createStripeCheckout = createAsyncThunk(
  'payment/createStripeCheckout',
  async (
    {
      priceId,
      mode,
      organizationId,
      email,
      useModal = true,
    }: {
      priceId: string;
      mode: 'subscription' | 'payment';
      organizationId: number | undefined;
      email: string | undefined;
      useModal?: boolean;
    },
    { rejectWithValue }
  ) => {
    try {
      const baseUrl = getBaseUrl();
      
      // Map mode to payment_type
      const payment_type = mode === 'subscription' ? 'subscription' : 'payg';
     
      const response = await axiosInstance.post(
        '/payments/create-checkout-session',
        {
          price_id: priceId,
          mode,
          organization_id: organizationId,
          email,
          payment_type,
          success_url: `${baseUrl}/dashboard/billing/success?session_id={CHECKOUT_SESSION_ID}`,
          cancel_url: `${baseUrl}/dashboard/billing/cancel`,
          ...(useModal ? { use_modal: true } : {}),
        }
      );
      
      return {
        ...response.data,
        useModal
      };
    } catch (error: any) {
      // Pass the full error response structure for better error handling
      const errorData = error.response?.data || { message: 'An error occurred' };
      return rejectWithValue(errorData);
    }
  }
);

// Initiate checkout with Stripe and either open modal or redirect to checkout page
export const initiateCheckout = createAsyncThunk(
  'payment/initiateCheckout',
  async (
    {
      priceId,
      mode,
      organizationId,
      email,
      useModal = true, // Default to modal for better UX
    }: {
      priceId: string;
      mode: 'subscription' | 'payment';
      organizationId: number | undefined;
      email: string | undefined;
      useModal?: boolean;
    },
    { dispatch, rejectWithValue }
  ) => {
    try {
      // First create the checkout session
      const checkoutResult = await dispatch(
        createStripeCheckout({
          priceId,
          mode,
          organizationId,
          email,
          useModal,
        })
      ).unwrap();

      // Load Stripe
      const stripe = await stripePromise;
      if (!stripe) {
        throw new Error('Stripe failed to load');
      }
     
      if (useModal) {
        
        // If using modal, we'll handle displaying the modal in the component
        // Just return the session ID and client secret
        return {
          sessionId: checkoutResult.sessionId || null,
          clientSecret: checkoutResult.clientSecret || null,
          useModal: true,
        };
      } else {
        // If not using modal, redirect to Stripe Checkout
        const { sessionId } = checkoutResult;
        if (!sessionId) {
          throw new Error('No session ID returned from checkout creation');
        }

        // Redirect to the Stripe checkout page
        const { error } = await stripe.redirectToCheckout({ sessionId });
        if (error) {
          throw error;
        }

        return { 
          success: true, 
          useModal: false 
        };
      }
    } catch (error: any) {
      console.error('Error initiating checkout:', error);
      return rejectWithValue(error.message || 'Failed to initiate checkout');
    }
  }
);

// The rest of the thunks remain largely the same since they interact with the Laravel backend
export const getSubscriptionStatus = createAsyncThunk(
  'payment/getSubscriptionStatus',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get('/payments/subscription-status');
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || 'An error occurred');
    }
  }
);

export const cancelSubscription = createAsyncThunk(
  'payment/cancelSubscription',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post('/payments/cancel-subscription');
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || 'An error occurred');
    }
  }
);

export const getUsageStats = createAsyncThunk(
  'payment/getUsageStats',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get('/payments/usage-stats');
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || 'An error occurred');
    }
  }
);



export const getPaygBalance = createAsyncThunk(
  'payment/getPaygBalance',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get('/payments/payg/balance');
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || 'An error occurred');
    }
  }
);

export const switchToPayg = createAsyncThunk(
  'payment/switchToPayg',
  async (organizationId: number, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(
        '/payments/billing/switch-to-payg',
        {
          organization_id: organizationId,
        }
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || 'An error occurred');
    }
  }
);

export const switchToSubscription = createAsyncThunk(
  'payment/switchToSubscription',
  async (
    {
      planId,
      organizationId,
    }: {
      planId: string;
      organizationId: number;
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await axiosInstance.post(
        '/payments/billing/switch-to-subscription',
        {
          plan_id: planId,
          organization_id: organizationId,
        }
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || 'An error occurred');
    }
  }
);

export const requestRefund = createAsyncThunk(
  'payment/requestRefund',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post('/payments/refund');
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || 'An error occurred');
    }
  }
);

// Handle Stripe webhook events
export const handleStripeWebhook = createAsyncThunk(
  'payment/handleStripeWebhook',
  async (
    {
      eventData,
      signature,
    }: {
      eventData: string;
      signature: string;
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await axiosInstance.post('/webhooks/stripe', eventData, {
        headers: {
          'Content-Type': 'application/json',
          'stripe-signature': signature,
        },
      });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || 'An error occurred');
    }
  }
);

// Verify a Stripe checkout session
export const verifyCheckoutSession = createAsyncThunk(
  'payment/verifyCheckoutSession',
  async (sessionId: string, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post('/payments/verify', {
        session_id: sessionId
      });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || 'An error occurred');
    }
  }
);

const paymentSlice = createSlice({
  name: 'payment',
  initialState,
  reducers: {
    clearCheckoutSession: (state) => {
      state.checkoutSessionId = null;
      state.clientSecret = null;
    },
    setCheckoutModalOpen: (state, action) => {
      state.isCheckoutModalOpen = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getSubscriptionStatus.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getSubscriptionStatus.fulfilled, (state, action) => {
     
        state.isLoading = false;
        state.subscriptionStatus = action.payload;
      })
      .addCase(getSubscriptionStatus.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      .addCase(getUsageStats.fulfilled, (state, action) => {
     
        state.usageStats = action.payload;
      })
      .addCase(getPaygBalance.fulfilled, (state, action) => {
        
        state.paygBalance = action.payload;
      })
      .addCase(switchToPayg.fulfilled, (state, action) => {
        state.subscriptionStatus = action.payload;
      })
      .addCase(switchToSubscription.fulfilled, (state, action) => {
        state.subscriptionStatus = action.payload;
      })
      .addCase(cancelSubscription.fulfilled, (state, action) => {
        state.subscriptionStatus = null;
      })
      .addCase(requestRefund.fulfilled, (state, action) => {
        if (action.payload.cancelled_subscription) {
          state.subscriptionStatus = null;
        }
      })
      .addCase(createStripeCheckout.pending, (state) => {
        state.isLoading = true;
        state.checkoutSessionId = null;
        state.clientSecret = null;
      })
      .addCase(createStripeCheckout.fulfilled, (state, action) => {
        state.isLoading = false;
        state.checkoutSessionId = action.payload.session_id || null;
      })
      .addCase(createStripeCheckout.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      .addCase(initiateCheckout.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(initiateCheckout.fulfilled, (state, action) => {
        state.isLoading = false;
        if (action.payload.useModal) {
          state.isCheckoutModalOpen = true;
          state.clientSecret = action.payload.clientSecret;
          state.checkoutSessionId = action.payload.sessionId;
        }
      })
      .addCase(initiateCheckout.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearCheckoutSession, setCheckoutModalOpen } = paymentSlice.actions;
export default paymentSlice.reducer;
